/**
 * Short UI strings for the shared marketing header, footer and cookie banner
 * in all six marketing languages. (Landing body copy: content/landingCopy*.ts.)
 */

import type { MarketingLanguage } from "./marketingCopy";

export interface MarketingUi {
  nav: { product: string; howItWorks: string; pricing: string; industries: string; appointments: string };
  /** Footer links to the sector landing pages. */
  sectors: { salon: string; marina: string };
  skipToContent: string;
  mainNav: string;
  mobileNav: string;
  language: string;
  login: string;
  bookDemo: string;
  startFree: string;
  openMenu: string;
  closeMenu: string;
  demoSubject: string;
  privacyPolicy: string;
  terms: string;
  general: string;
  sales: string;
  companyLine: string;
  privacyShort: string;
  termsShort: string;
  cookiePreferences: string;
  lightTheme: string;
  darkTheme: string;
  cookieText: string;
  reject: string;
  accept: string;
}

export const marketingUi: Record<MarketingLanguage, MarketingUi> = {
  tr: {
    nav: { product: "Ürün", howItWorks: "Nasıl çalışır", pricing: "Fiyatlar", industries: "Sektörler", appointments: "Randevu" },
    sectors: { salon: "Kuaför ve güzellik", marina: "Marinalar" },
    skipToContent: "İçeriğe geç",
    mainNav: "Ana menü",
    mobileNav: "Mobil menü",
    language: "Dil",
    login: "Giriş",
    bookDemo: "Demo planla",
    startFree: "Ücretsiz dene",
    openMenu: "Menüyü aç",
    closeMenu: "Menüyü kapat",
    demoSubject: "Hümo demo talebi",
    privacyPolicy: "Gizlilik Politikası",
    terms: "Kullanım Şartları",
    general: "Genel",
    sales: "Satış",
    companyLine: "TUBU ARGE A.Ş. · MERSİS: 0859065580900001 · Pendik V.D. 8590655809 · Teknopark İstanbul, 1/4C No:307, Pendik/İstanbul · 0850 840 1859",
    privacyShort: "Gizlilik",
    termsShort: "Şartlar",
    cookiePreferences: "Çerez Tercihleri",
    lightTheme: "Açık tema",
    darkTheme: "Koyu tema",
    cookieText:
      "Bu site, deneyimi geliştirmek için zorunlu çerezler kullanır ve onayınızla analitik/pazarlama çerezleri yükler. İstediğiniz zaman tercihinizi değiştirebilirsiniz.",
    reject: "Reddet",
    accept: "Kabul Et",
  },
  en: {
    nav: { product: "Product", howItWorks: "How it works", pricing: "Pricing", industries: "Industries", appointments: "Appointments" },
    sectors: { salon: "Hair & beauty salons", marina: "Marinas" },
    skipToContent: "Skip to content",
    mainNav: "Main",
    mobileNav: "Mobile",
    language: "Language",
    login: "Log in",
    bookDemo: "Book a demo",
    startFree: "Start free",
    openMenu: "Open menu",
    closeMenu: "Close menu",
    demoSubject: "Hümo demo request",
    privacyPolicy: "Privacy Policy",
    terms: "Terms of Service",
    general: "General",
    sales: "Sales",
    companyLine: "TUBU ARGE A.Ş. · MERSIS: 0859065580900001 · Tax ID 8590655809 (Pendik) · Teknopark İstanbul, 1/4C No:307, Pendik/Istanbul, Türkiye · +90 850 840 1859",
    privacyShort: "Privacy",
    termsShort: "Terms",
    cookiePreferences: "Cookie Preferences",
    lightTheme: "Light theme",
    darkTheme: "Dark theme",
    cookieText:
      "We use strictly necessary cookies, plus analytics & marketing cookies with your consent. You can change your mind anytime.",
    reject: "Reject",
    accept: "Accept",
  },
  ru: {
    nav: { product: "Продукт", howItWorks: "Как это работает", pricing: "Цены", industries: "Отрасли", appointments: "Запись" },
    sectors: { salon: "Салоны красоты", marina: "Марины" },
    skipToContent: "Перейти к содержанию",
    mainNav: "Главное меню",
    mobileNav: "Мобильное меню",
    language: "Язык",
    login: "Войти",
    bookDemo: "Запросить демо",
    startFree: "Попробовать",
    openMenu: "Открыть меню",
    closeMenu: "Закрыть меню",
    demoSubject: "Запрос демо Hümo",
    privacyPolicy: "Политика конфиденциальности",
    terms: "Условия использования",
    general: "Общие вопросы",
    sales: "Продажи",
    companyLine: "TUBU ARGE A.Ş. · MERSIS: 0859065580900001 · Tax ID 8590655809 (Pendik) · Teknopark İstanbul, 1/4C No:307, Pendik/Istanbul, Türkiye · +90 850 840 1859",
    privacyShort: "Конфиденциальность",
    termsShort: "Условия",
    cookiePreferences: "Настройки cookie",
    lightTheme: "Светлая тема",
    darkTheme: "Тёмная тема",
    cookieText:
      "Мы используем необходимые файлы cookie, а аналитические и маркетинговые — только с вашего согласия. Вы можете изменить выбор в любой момент.",
    reject: "Отклонить",
    accept: "Принять",
  },
  de: {
    nav: { product: "Produkt", howItWorks: "So funktioniert's", pricing: "Preise", industries: "Branchen", appointments: "Termine" },
    sectors: { salon: "Friseur- & Kosmetiksalons", marina: "Marinas" },
    skipToContent: "Zum Inhalt springen",
    mainNav: "Hauptmenü",
    mobileNav: "Mobiles Menü",
    language: "Sprache",
    login: "Anmelden",
    bookDemo: "Demo buchen",
    startFree: "Kostenlos testen",
    openMenu: "Menü öffnen",
    closeMenu: "Menü schließen",
    demoSubject: "Hümo Demo-Anfrage",
    privacyPolicy: "Datenschutzerklärung",
    terms: "Nutzungsbedingungen",
    general: "Allgemein",
    sales: "Vertrieb",
    companyLine: "TUBU ARGE A.Ş. · MERSIS: 0859065580900001 · Tax ID 8590655809 (Pendik) · Teknopark İstanbul, 1/4C No:307, Pendik/Istanbul, Türkiye · +90 850 840 1859",
    privacyShort: "Datenschutz",
    termsShort: "AGB",
    cookiePreferences: "Cookie-Einstellungen",
    lightTheme: "Helles Design",
    darkTheme: "Dunkles Design",
    cookieText:
      "Wir verwenden notwendige Cookies sowie Analyse- und Marketing-Cookies nur mit Ihrer Einwilligung. Sie können Ihre Wahl jederzeit ändern.",
    reject: "Ablehnen",
    accept: "Akzeptieren",
  },
  fr: {
    nav: { product: "Produit", howItWorks: "Fonctionnement", pricing: "Tarifs", industries: "Secteurs", appointments: "Rendez-vous" },
    sectors: { salon: "Salons de coiffure et beauté", marina: "Marinas" },
    skipToContent: "Aller au contenu",
    mainNav: "Menu principal",
    mobileNav: "Menu mobile",
    language: "Langue",
    login: "Connexion",
    bookDemo: "Demander une démo",
    startFree: "Essai gratuit",
    openMenu: "Ouvrir le menu",
    closeMenu: "Fermer le menu",
    demoSubject: "Demande de démo Hümo",
    privacyPolicy: "Politique de confidentialité",
    terms: "Conditions d'utilisation",
    general: "Général",
    sales: "Ventes",
    companyLine: "TUBU ARGE A.Ş. · MERSIS: 0859065580900001 · Tax ID 8590655809 (Pendik) · Teknopark İstanbul, 1/4C No:307, Pendik/Istanbul, Türkiye · +90 850 840 1859",
    privacyShort: "Confidentialité",
    termsShort: "Conditions",
    cookiePreferences: "Préférences cookies",
    lightTheme: "Thème clair",
    darkTheme: "Thème sombre",
    cookieText:
      "Nous utilisons des cookies strictement nécessaires, et des cookies d'analyse et marketing uniquement avec votre accord. Vous pouvez changer d'avis à tout moment.",
    reject: "Refuser",
    accept: "Accepter",
  },
  it: {
    nav: { product: "Prodotto", howItWorks: "Come funziona", pricing: "Prezzi", industries: "Settori", appointments: "Appuntamenti" },
    sectors: { salon: "Parrucchieri ed estetica", marina: "Marina" },
    skipToContent: "Vai al contenuto",
    mainNav: "Menu principale",
    mobileNav: "Menu mobile",
    language: "Lingua",
    login: "Accedi",
    bookDemo: "Prenota una demo",
    startFree: "Prova gratis",
    openMenu: "Apri menu",
    closeMenu: "Chiudi menu",
    demoSubject: "Richiesta demo Hümo",
    privacyPolicy: "Informativa sulla privacy",
    terms: "Termini di servizio",
    general: "Generale",
    sales: "Vendite",
    companyLine: "TUBU ARGE A.Ş. · MERSIS: 0859065580900001 · Tax ID 8590655809 (Pendik) · Teknopark İstanbul, 1/4C No:307, Pendik/Istanbul, Türkiye · +90 850 840 1859",
    privacyShort: "Privacy",
    termsShort: "Termini",
    cookiePreferences: "Preferenze cookie",
    lightTheme: "Tema chiaro",
    darkTheme: "Tema scuro",
    cookieText:
      "Utilizziamo cookie strettamente necessari e, solo con il tuo consenso, cookie di analisi e marketing. Puoi cambiare idea in qualsiasi momento.",
    reject: "Rifiuta",
    accept: "Accetta",
  },
};

export const getMarketingUi = (language: string): MarketingUi =>
  marketingUi[language as MarketingLanguage] ?? marketingUi.en;
